import { useMemo } from 'react'
import { pick } from 'lodash'
import { NlpExample } from '@feedyou-ai/feedbot-schema'
import { isNotEmptyObject, CloudServiceSource } from '@feedyou/utils'

import { useBotId } from '../../../../store/flow'
import { useNlpModelForIntentId } from '../hooks'
import { Props } from '../UnmatchedUtteranceSettingsPopup'
import { useOpenModalDescriptor } from '../../../../store/modals'
import { useStableObject } from '../../../../lib/object/useStableObject'
import { useCloudServices } from '../../../../features/cloudServices/hooks'
import { toastNotifyIntentDescriptionSuggestionRecieveSuccess } from './toast'
import { nlpTrainingApi as api } from '../../../../features/nlpTraining/nlpTrainingApi'
import { SuggestDescriptionQueryArg as QueryArg } from '../../../../features/nlpTraining/types'

export function useSuggestEnrichedDescription(params: {
  prompt: string
  description: string
  utterance: NlpExample
  llmChatService: string
  onChangeTextOutput: (textOutput: string) => void
}) {
  const botId = useBotId()
  const descriptor = useNlpModelAndIntentFromModalDescriptor()

  const { prompt, utterance, description, llmChatService, onChangeTextOutput } = params
  const provider = { ...pick(descriptor.model?.provider, ['type', 'tools']), llmChatService }
  const model = { ...descriptor.model, provider, intents: [{ ...descriptor.intent, description }] }

  const arg = { botId, model, prompt, utterance, intentId: descriptor.intent.id } as QueryArg
  const [suggestDescription, queryResult] = api.endpoints.suggestEnrichedDescription.useLazyQuery()
  return {
    queryResult,
    suggestDescription: () =>
      suggestDescription(arg)
        .unwrap()
        .then(({ textOutput }) => onChangeTextOutput(textOutput))
        .then(() => toastNotifyIntentDescriptionSuggestionRecieveSuccess())
        .catch(error => console.error(error?.message))
  }
}

export function useNlpModelAndIntentFromModalDescriptor() {
  const { intent } = useOpenModalDescriptor() as Props['modalDescriptor']
  const model = useNlpModelForIntentId(intent.id)

  return { model, intent }
}

export function useExistsDefaultCloudServices(source: CloudServiceSource) {
  const cloudServices = useCloudServices(source)
  const defaultByKind = useStableObject(cloudServices.defaultByKind || {})
  const defaultByAction = useStableObject(cloudServices.defaultByAction || {})

  return useMemo(
    () =>
      Object.values(defaultByKind).some(isNotEmptyObject) ||
      Object.values(defaultByAction).some(isNotEmptyObject),
    [defaultByKind, defaultByAction]
  )
}
